/**
 * Servicio de gestión del catálogo de impuestos.
 * Requiere cliente autenticado de Supabase como primer parámetro.
 *
 * @example
 * const { supabase } = useSupabase()
 * await catalogoImpuestosService.obtenerCatalogo(supabase!, organizacionId)
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { CatalogoImpuesto, CategoriaImpuestoCatalogo, ColorImpuesto } from '@/types/impuestos'

// -----------------------------------------
// Obtener catalogo completo (globales + per-org, dedup por codigo)
// -----------------------------------------
export async function obtenerCatalogoImpuestos(
  client: SupabaseClient<Database>,
  organizacionId: string,
  soloActivos: boolean = true
): Promise<CatalogoImpuesto[]> {

  let query = client
    .from('catalogo_impuestos')
    .select('*')
    .or(`organizacion_id.is.null,organizacion_id.eq.${organizacionId}`)
    .order('orden', { ascending: true })
    .order('nombre', { ascending: true })

  if (soloActivos) {
    query = query.eq('activo', true)
  }

  const { data, error } = await query

  if (error) throw error

  // Dedup by codigo: per-org (has organizacion_id) wins over global
  const merged = new Map<string, CatalogoImpuesto>()
  for (const imp of (data as CatalogoImpuesto[]) ?? []) {
    const existing = merged.get(imp.codigo)
    if (!existing || (imp.organizacion_id && !existing.organizacion_id)) {
      merged.set(imp.codigo, imp)
    }
  }

  return Array.from(merged.values())
}

// -----------------------------------------
// Crear nuevo impuesto en catalogo
// -----------------------------------------
export async function crearImpuesto(
  client: SupabaseClient<Database>,
  organizacionId: string,
  input: {
    codigo?: string
    nombre: string
    nombre_corto?: string
    categoria?: CategoriaImpuestoCatalogo
    jurisdiccion?: string
    color?: ColorImpuesto
  }
): Promise<CatalogoImpuesto> {

  // Generar codigo si no se proporciona
  const codigo = input.codigo || input.nombre.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')

  // is_global=false y source='manual' se asignan por default en BD
  const { data, error } = await client
    .from('catalogo_impuestos')
    .insert({
      organizacion_id: organizacionId,
      codigo,
      nombre: input.nombre,
      nombre_corto: input.nombre_corto || input.nombre.substring(0, 12).toUpperCase(),
      categoria: input.categoria || 'otros',
      jurisdiccion: input.jurisdiccion || null,
      color: input.color || 'gray',
    })
    .select()
    .single()

  if (error) throw error
  return data as CatalogoImpuesto
}

// -----------------------------------------
// Actualizar impuesto del catalogo
// -----------------------------------------
export async function actualizarImpuesto(
  client: SupabaseClient<Database>,
  organizacionId: string,
  id: string,
  input: Partial<{
    nombre: string
    nombre_corto: string
    categoria: CategoriaImpuestoCatalogo
    jurisdiccion: string
    color: ColorImpuesto
    orden: number
    activo: boolean
  }>
): Promise<CatalogoImpuesto> {

  // Defensa en profundidad: filtrar por organizacion_id además de id
  const { data, error } = await client
    .from('catalogo_impuestos')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .eq('organizacion_id', organizacionId)
    .select()
    .single()

  if (error) throw error
  return data as CatalogoImpuesto
}

// -----------------------------------------
// Desactivar impuesto (soft delete)
// -----------------------------------------
export async function desactivarImpuesto(
  client: SupabaseClient<Database>,
  organizacionId: string,
  id: string
): Promise<void> {

  // Defensa en profundidad: filtrar por organizacion_id además de id
  const { error } = await client
    .from('catalogo_impuestos')
    .update({ activo: false, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('organizacion_id', organizacionId)

  if (error) throw error
}

// -----------------------------------------
// Inicializar catalogo para organizacion
// -----------------------------------------
export async function inicializarCatalogo(
  client: SupabaseClient<Database>,
  organizacionId: string
): Promise<void> {

  const { error } = await client.rpc('seed_catalogo_impuestos', {
    p_organizacion_id: organizacionId,
  })

  if (error) throw error
}

// -----------------------------------------
// Objeto exportado para compatibilidad
// -----------------------------------------
export const catalogoImpuestosService = {
  obtenerCatalogo: obtenerCatalogoImpuestos,
  crear: crearImpuesto,
  actualizar: actualizarImpuesto,
  desactivar: desactivarImpuesto,
  inicializar: inicializarCatalogo,
}
